import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logoLight from "@/assets/mark-tognoli-realty-logo-light.png";
import logoDark from "@/assets/mark-tognoli-realty-logo.png";

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Buy", href: "/buy" },
  { label: "Sell", href: "/sell" },
  {
    label: "Resources",
    href: "#",
    children: [
      { label: "Buyer Guide", href: "/buyer-guide" },
      { label: "Seller Guide", href: "/seller-guide" },
    ],
  },
  { label: "Contact", href: "/contact" },
];

interface NavigationProps {
  /** When true, nav starts transparent over a hero and turns solid on scroll. */
  transparentOnTop?: boolean;
}

const Navigation = ({ transparentOnTop = false }: NavigationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!transparentOnTop);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!transparentOnTop) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOnTop]);

  const isSolid = scrolled;
  const useLightLogo = transparentOnTop && !isSolid;

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isSolid
          ? "bg-background/95 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto max-w-[1400px] px-6">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" aria-label="Mark Tognoli Realty home" className="flex items-center">
            <img
              src={useLightLogo ? logoLight : logoDark}
              alt="Mark Tognoli Realty — San Francisco Bay Area Luxury Real Estate"
              width={220}
              height={76}
              className="h-16 md:h-20 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => {
              const active = location.pathname === item.href;
              const isResources = item.label === "Resources";

              if (isResources) {
                return (
                  <div
                    key={item.href}
                    className="relative"
                    onMouseEnter={() => setResourcesOpen(true)}
                    onMouseLeave={() => setResourcesOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 ${
                        isSolid
                          ? "text-foreground hover:text-primary"
                          : "text-white/85 hover:text-brand-ivory"
                      }`}
                      aria-expanded={resourcesOpen}
                      aria-haspopup="true"
                    >
                      {item.label}
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-200 ${
                          resourcesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {resourcesOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-56 z-50">
                        <div className="bg-background/95 backdrop-blur-md border border-border/40 rounded-sm shadow-xl py-3">
                          {item.children?.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setResourcesOpen(false)}
                              className={`block px-5 py-2.5 text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors ${
                                location.pathname === child.href
                                  ? "text-primary"
                                  : "text-foreground hover:text-primary hover:bg-accent/40"
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`text-[11px] tracking-[0.3em] uppercase font-semibold transition-colors duration-300 ${
                    isSolid
                      ? active
                        ? "text-primary"
                        : "text-foreground hover:text-primary"
                      : active
                        ? "text-brand-ivory"
                        : "text-white/85 hover:text-brand-ivory"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <a
            href="tel:+14157106922"
            className={`hidden md:inline-block text-[11px] tracking-[0.3em] uppercase font-semibold border px-5 py-2.5 transition-all duration-300 ${
              isSolid
                ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                : "border-white/40 text-white hover:bg-white hover:text-foreground"
            }`}
          >
            (415) 710-6922
          </a>

          <button
            className={`md:hidden p-2 ${isSolid ? "text-foreground" : "text-white"}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden bg-background border-t border-border py-6 space-y-2">
            {navItems.map((item) => {
              if (item.children) {
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                      className="flex items-center justify-between w-full text-sm tracking-[0.3em] uppercase font-semibold text-foreground hover:text-primary py-2"
                      aria-expanded={mobileResourcesOpen}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          mobileResourcesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {mobileResourcesOpen && (
                      <div className="pl-5 space-y-2 border-l border-border/50 ml-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => {
                              setIsOpen(false);
                              setMobileResourcesOpen(false);
                            }}
                            className={`block text-xs tracking-[0.3em] uppercase font-semibold py-1.5 transition-colors ${
                              location.pathname === child.href
                                ? "text-primary"
                                : "text-foreground/70 hover:text-primary"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="block text-sm tracking-[0.3em] uppercase font-semibold text-foreground hover:text-primary py-2"
                >
                  {item.label}
                </Link>
              );
            })}
            <a
              href="tel:+14157106922"
              className="block text-sm tracking-[0.3em] uppercase font-semibold text-primary py-2"
            >
              (415) 710-6922
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
